import React, {useMemo} from 'react'
import {Helmet} from 'react-helmet'
import {useLocation} from 'react-router-dom'
import {Box, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {getAppDomain} from '../../../utils/utils'

import {SEARCH_PARAMS_LIMIT} from '../../../constants'

/**
 * PaginationMetaTags is a React functional component that generates pagination-related meta tags
 * and links for a product list page. It calculates and renders the appropriate "prev", "next",
 * and "canonical" links for search engine optimization (SEO) purposes, based on the current page
 * and total number of items.
 *
 * @param {Object} props - The component props.
 * @param {number} props.total - The total number of items available for pagination.
 * @param {number} props.limitValue - The number of items displayed per page.
 *
 * @returns {JSX.Element} A React fragment containing Helmet meta tags for pagination and a hidden
 * Box component with pagination links.
 *
 * The component uses the `useLocation` hook to access the current URL location and query parameters.
 * It calculates the base URL, previous page URL, next page URL, and canonical URL based on the current
 * page parameter. The total number of pages is determined by dividing the total number of items by the
 * limit value.
 *
 * The `useMemo` hook is used to efficiently generate an array of pagination links, which are rendered
 * as hidden links within a Box component. These links are useful for accessibility and SEO purposes.
 *
 * The Helmet component is used to inject the calculated meta tags into the document head, ensuring
 * that search engines can correctly index the paginated content.
 */
const PaginationMetaTags = ({total}) => {
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    const pageParam = parseInt(urlParams.get('p')) || 0

    const baseUrl = `${getAppDomain()}${location.pathname}`
    const prevPageUrl =
        pageParam > 1 ? `${baseUrl}?p=${pageParam - 1}` : pageParam === 1 ? baseUrl : null
    const nextPageUrl =
        pageParam < Math.ceil(total / SEARCH_PARAMS_LIMIT) - 1
            ? `${baseUrl}?p=${pageParam + 1}`
            : pageParam === 0
            ? `${baseUrl}?p=1`
            : null

    const canonicalUrl = pageParam === 0 ? baseUrl : `${baseUrl}?p=${pageParam}`

    // Calculate totalPages based on the current limitValue
    const totalPages = Math.ceil(total / SEARCH_PARAMS_LIMIT)

    // Use useMemo to recalculate pagination links when location or totalPages change
    const paginationLinks = useMemo(() => {
        return Array.from({length: totalPages}, (_, index) => {
            const pageParam = index === 0 ? '' : `?p=${index}`
            const url = `${baseUrl}${pageParam}`
            return (
                <Link key={index} href={url} display="block">
                    {url}
                </Link>
            )
        })
    }, [baseUrl, totalPages])

    return (
        <>
            <Helmet>
                {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
                {nextPageUrl && <link rel="next" href={nextPageUrl} />}
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <Box display="none">{paginationLinks}</Box>
        </>
    )
}

PaginationMetaTags.propTypes = {
    total: PropTypes.number.isRequired
}

export default PaginationMetaTags
